import React, { ChangeEvent, FocusEvent, useEffect, useRef } from 'react';

import { ReactComponent as ChevronLeft } from '/src/images/icons/chevron-left-small.svg';
import { ReactComponent as Clear } from '/src/images/icons/close-normal.svg';
import { ReactComponent as Search } from '/src/images/icons/search-small.svg';
import { ReactComponent as WarningIcon } from '/src/images/icons/warning.svg';
export type InputFieldTrailingIcon = 'Search' | 'ClearButton';
export type InputFieldInitialIcon = 'Back';

export interface InputFieldProps {
  name: string;
  id: string;
  placeholder?: string;
  disabled?: boolean;
  type?: string;
  label: string;
  labelClassName?: string;
  className?: string;
  classNameInput?: string;
  value?: string | number;
  defaultValue?: string | number;
  icon?: InputFieldTrailingIcon;
  onChange: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  autoComplete?: boolean;
  autoFocus?: boolean;
  isTextarea?: boolean;
  required?: boolean;
  maxLength?: number;
  minLength?: number;
  min?: number;
  max?: number;
  step?: number;
  pattern?: string;
  hasError?: boolean;
  errorMessage?: string;
  list?: string;
  visuallyHideLabel?: boolean;
  displayWarningIconInErrorMessage?: boolean;
  clearField?: () => void;
  leftSideIcon?: InputFieldInitialIcon;
  leftSideIconOnClick?: () => void;
}

const InputField = ({
  name,
  id,
  placeholder,
  disabled = false,
  type,
  label,
  labelClassName = '',
  className,
  classNameInput,
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  icon,
  autoComplete = true,
  autoFocus = false,
  isTextarea = false,
  required = false,
  maxLength,
  minLength,
  min,
  max,
  step,
  pattern,
  hasError = false,
  errorMessage = '',
  list,
  visuallyHideLabel = false,
  displayWarningIconInErrorMessage = false,
  clearField,
  leftSideIcon,
  leftSideIconOnClick,
}: InputFieldProps) => {
  const LeftSideIcon = () => {
    switch (leftSideIcon) {
      case 'Back':
        return (
          <ChevronLeft className="h-6 w-6 cursor-pointer text-neutral-grey-400" />
        );
      default:
        return null;
    }
  };
  const InputFieldIcon = () => {
    switch (icon) {
      case 'Search':
        return <Search className="h-4 w-4 text-neutral-grey-400" />;
      case 'ClearButton':
        return (
          <button onClick={() => clearField?.()}>
            <Clear className="text-neutral-grey-40 h-4 w-4" />
          </button>
        );
      default:
        return null;
    }
  };

  //Pattern validate textarea field, if there is a ref and a pattern
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (textAreaRef.current && pattern) {
      const textareaValue = textAreaRef.current.value;
      const textareaRegEx = new RegExp(pattern);
      const textareaRegExError = textareaRegEx.test(textareaValue);
      if (textareaRegExError) {
        textAreaRef.current.setCustomValidity(
          `Pattern error: textarea does not conform to pattern: ${pattern}`,
        );
      } else {
        {
          textAreaRef.current.setCustomValidity('');
        }
      }
    }
  }, [textAreaRef.current?.value, pattern]);

  const inputBaseClasses =
    'w-full text-bodyl focus-within:outline focus-within:outline-offset-3 focus-within:outline-3 focus-within:outline-text-light appearance-none border border-neutral-grey-200 py-2.5 pr-6 pl-4 disabled:border-disabled-light disabled:text-disabled-light ';

  const inputErrorClasses = 'invalid:border-semantic-negative';

  const inputAutoFocus = () => {
    return autoFocus ? { autoFocus: true } : {};
  };

  const inputList = () => {
    return list ? { list: list } : {};
  };

  return (
    <div className={`${className ? className : ''}`}>
      <label
        htmlFor={id}
        className={`mb-2 ${disabled ? 'text-disabled-light' : 'text-bodys'} ${
          visuallyHideLabel ? 'hidden' : 'inline-block'
        } ${labelClassName}`}
      >
        {label}
        {required && '*'}
      </label>

      {!isTextarea && (
        <input
          tabIndex={0}
          maxLength={maxLength}
          pattern={pattern}
          minLength={minLength}
          min={min}
          max={max}
          step={step}
          id={id}
          name={name}
          disabled={disabled}
          type={type}
          placeholder={placeholder}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          onBlur={(e) => onBlur?.(e)}
          onFocus={(e) => onFocus?.(e)}
          className={`${inputBaseClasses} ${
            hasError ? inputErrorClasses : ''
          } rounded-full ${classNameInput} ${leftSideIcon ? 'pl-8' : ''}`}
          autoComplete={autoComplete ? 'on' : 'off'}
          required={required}
          {...inputAutoFocus()}
          {...inputList()}
        />
      )}
      {leftSideIcon && !isTextarea && (
        <div
          className="absolute left-2 top-2/4 -translate-y-2/4"
          onClick={leftSideIconOnClick}
        >
          <LeftSideIcon />
        </div>
      )}
      {icon && !isTextarea && (
        <div className="absolute right-4 top-2/4 flex -translate-y-2/4">
          <InputFieldIcon />
        </div>
      )}
      {isTextarea && (
        <textarea
          tabIndex={0}
          id={id}
          rows={5}
          value={value}
          name={name}
          disabled={disabled}
          autoComplete={autoComplete ? 'on' : 'off'}
          className={`${inputBaseClasses} ${
            hasError ? inputErrorClasses : ''
          } rounded-xl`}
          required={required}
          onChange={onChange}
          onBlur={(e) => onBlur?.(e)}
          onFocus={(e) => onFocus?.(e)}
          maxLength={maxLength}
          ref={textAreaRef}
        ></textarea>
      )}
      {hasError && (
        <p className="mt-2 text-bodys text-semantic-negative">
          {displayWarningIconInErrorMessage && (
            <WarningIcon className="mr-1 inline align-[-1px]" />
          )}
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default InputField;
